import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hospital, Stethoscope, Building2, Info, ArrowUpRight, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import DataFreshnessStrip from '../components/DataFreshnessStrip';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function KPI({ label, value, accent }) {
  return (
    <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-3xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

export default function HospitalIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.hospitalDashboard()
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e?.response?.data?.detail || 'Dashboard not available'); setLoading(false); });
  }, []);

  if (loading) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading Hospital Intelligence…</div></main></div>;
  if (error || !data) return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10">
        <h1 className="font-display text-3xl text-[#1A2622]">Hospital Intelligence</h1>
        <p className="mt-3 text-[13px] text-[#7A857F]">{error || 'Data not loaded yet.'}</p>
      </main>
    </div>
  );

  if (data.ready === false) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10">
          <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospital Intelligence' }]} />
          <h1 className="font-display text-4xl text-[#1A2622] mt-2">Hospital Intelligence</h1>
          <div className="mt-6 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-8 text-center">
            <Hospital size={28} className="mx-auto text-[#A8B0AA]" />
            <p className="mt-3 text-[13px] text-[#7A857F]">{data.message}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospital Intelligence' }]} />

        <div className="mt-3">
          <DataFreshnessStrip />
        </div>

        <header className="mt-4 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
              <Hospital size={11} /> Hospital × SNF × Physician
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">Hospital Intelligence</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[680px]">
              Acute-care hospitals mapped against the SNF base. Surfaces referral catchment, shared rounding
              physicians (DAC-confirmed + geographic-likely), and quality alignment for every market.
            </p>
          </div>
          <Link to="/hospitals" className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#34538F]" data-testid="hosp-go-directory">
            Hospital directory <ArrowUpRight size={13} />
          </Link>
        </header>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hosp-intel-kpis">
          <KPI label="Hospitals indexed" value={fmtInt(data.hospitals_indexed)} accent="text-[#34538F]" />
          <KPI label="Hospital ↔ SNF links" value={fmtInt(data.facility_hospital_links)} accent="text-[#2D5F4F]" />
          <KPI label="Facilities with summary" value={fmtInt(data.facilities_with_summary)} />
          <KPI label="States covered" value={fmtInt(data.states_covered)} accent="text-[#A06628]" />
        </div>

        {/* Top facilities */}
        <section className="mt-7 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0E7D2]">
            <h2 className="font-display text-lg font-semibold text-[#1A2622]">Top facilities by market alignment</h2>
            <p className="text-[11.5px] text-[#7A857F]">Combined score of nearby-hospital density, ratings, confirmed-shared physicians, specialty coverage, and system diversity.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Facility</th>
                  <th className="text-left px-4 py-2.5">State</th>
                  <th className="text-right px-4 py-2.5">Nearby hospitals</th>
                  <th className="text-right px-4 py-2.5">Confirmed shared</th>
                  <th className="text-right px-4 py-2.5">Likely shared</th>
                  <th className="text-right px-4 py-2.5">Top hospital</th>
                  <th className="text-right px-4 py-2.5">Score</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_facilities || []).map((f) => (
                  <tr key={f.ccn} className="border-t border-[#F0E7D2]" data-testid={`top-fac-${f.ccn}`}>
                    <td className="px-4 py-2.5"><Link to={`/facilities/${f.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{f.facility_name}</Link><div className="text-[10.5px] text-[#7A857F]">CCN {f.ccn}</div></td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{f.facility_state}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(f.nearby_hospitals)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#2D5F4F] tabular-nums">{fmtInt(f.confirmed_shared_physicians)}</td>
                    <td className="px-4 py-2.5 text-right text-[#A06628] tabular-nums">{fmtInt(f.likely_shared_physicians)}</td>
                    <td className="px-4 py-2.5 text-right text-[#3A4A44] truncate max-w-[200px]" title={f.top_hospital}>{f.top_hospital || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#34538F]">{f.market_alignment_score?.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Top hospitals + state leaderboard side-by-side */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <section className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2]">
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Top hospitals by SNF reach</h2>
              <p className="text-[11.5px] text-[#7A857F]">Hospitals connected to the most SNFs by ZIP / city catchment.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr><th className="text-left px-4 py-2.5">Hospital</th><th className="text-left px-4 py-2.5">St</th><th className="text-right px-4 py-2.5">★</th><th className="text-right px-4 py-2.5">SNFs</th></tr>
                </thead>
                <tbody>
                  {(data.top_hospitals_by_reach || []).map((h) => (
                    <tr key={h.ccn} className="border-t border-[#F0E7D2]" data-testid={`top-hosp-${h.ccn}`}>
                      <td className="px-4 py-2.5"><Link to={`/hospitals/${h.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{h.name}</Link></td>
                      <td className="px-4 py-2.5 text-[#3A4A44]">{h.state}</td>
                      <td className="px-4 py-2.5 text-right text-[#A06628] font-semibold">{h.rating ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{fmtInt(h.snfs_reached)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2]">
              <h2 className="font-display text-base font-semibold text-[#1A2622]">State leaderboard</h2>
              <p className="text-[11.5px] text-[#7A857F]">Total nearby-hospital links + average hospital rating per state.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr><th className="text-left px-4 py-2.5">State</th><th className="text-right px-4 py-2.5">SNFs</th><th className="text-right px-4 py-2.5">Hosp links</th><th className="text-right px-4 py-2.5">Avg ★</th></tr>
                </thead>
                <tbody>
                  {(data.state_leaderboard || []).map((s) => (
                    <tr key={s.state} className="border-t border-[#F0E7D2]" data-testid={`top-state-${s.state}`}>
                      <td className="px-4 py-2.5 font-semibold text-[#1A2622]">{s.state}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.facilities)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.nearby_hospitals_total)}</td>
                      <td className="px-4 py-2.5 text-right text-[#A06628] font-semibold">{s.avg_hospital_rating ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Methodology */}
        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Source:</strong> {data.source}. <strong>Secondary:</strong> {data.secondary_source}.
            All linkages are public-data estimates derived from CMS Facility Affiliation + geographic catchment.
            <ShieldCheck size={11} className="inline mx-1 text-[#2D5F4F]" /> No patient-level referrals.
          </span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
